import { FC, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Modal, TextArea } from 'src/components';
import UserReportsApi from 'src/api/userReports';
import { UserReportRequest } from 'src/api/userReports/types';
import { MemriseApiError } from 'src/api/errors';
import * as S from './styles';

const msgs = defineMessages({
  title: { id: 'report_issue_modal_title' },
  placeholder: { id: 'report_issue_modal_placeholder' },
  placeholderSourceLanguageEn: {
    id: 'report_issue_modal_placeholder_source_language_en',
    defaultMessage:
      'Describe any problems with the word/phrase/sentence, example sentences, Mems, or media (video/audio)',
  },
  send: { id: 'report_issue_modal_send' },
  cancel: { id: 'report_issue_modal_cancel' },
  success: { id: 'report_issue_modal_success' },
  error: { id: 'report_issue_modal_error' },
  errorRateLimit: { id: 'report_issue_modal_error_rate_limit' },
  learnableInfo: { id: 'report_issue_modal_learnable_info' },
  characterCount: { id: 'report_issue_modal_character_count' },
});

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  learnableId?: number;
  learnableText?: string;
  onSuccess?: () => void;
  isEnglishSource?: boolean;
}

export const ReportIssueModal: FC<ReportIssueModalProps> = ({
  isOpen,
  onClose,
  learnableId,
  learnableText,
  onSuccess,
  isEnglishSource = false,
}) => {
  const intl = useIntl();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const characterCount = message.length;
  const isMessageValid = characterCount > 0 && characterCount <= 500;

  const handleSubmit = async () => {
    if (!isMessageValid || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const request: UserReportRequest = {
        message,
        learnable_id: learnableId,
        origin: 'Web',
      };

      await UserReportsApi.submitReport(request);
      setMessage('');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      if (error instanceof MemriseApiError) {
        if (error.message === 'rate_limit_exceeded') {
          setErrorMessage(intl.formatMessage(msgs.errorRateLimit));
        } else {
          setErrorMessage(intl.formatMessage(msgs.error));
        }
      } else {
        setErrorMessage(intl.formatMessage(msgs.error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setMessage('');
      setErrorMessage('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      shouldCloseOnOutsideClick
      shouldCloseOnEsc
      darkCloseIcon
    >
      <S.Container>
        <S.Title>{intl.formatMessage(msgs.title)}</S.Title>

        {learnableText && (
          <S.LearnableInfo>
            <S.LearnableLabel>{intl.formatMessage(msgs.learnableInfo)}</S.LearnableLabel>
            <S.LearnableDisplay>
              {learnableText.split(' - ').map((part, index, parts) => (
                <S.LearnablePart key={part.trim()} $isDefinition={index === parts.length - 1}>
                  {part.trim()}
                </S.LearnablePart>
              ))}
            </S.LearnableDisplay>
          </S.LearnableInfo>
        )}

        <S.TextAreaWrapper>
          <TextArea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={intl.formatMessage(
              isEnglishSource ? msgs.placeholderSourceLanguageEn : msgs.placeholder,
            )}
            disabled={isSubmitting}
            rows={4}
            maxLength={500}
          />
          <S.CharacterCount $isOverLimit={characterCount > 500}>
            {intl.formatMessage(msgs.characterCount, { count: characterCount })}
          </S.CharacterCount>
        </S.TextAreaWrapper>

        {errorMessage && <S.ErrorMessage>{errorMessage}</S.ErrorMessage>}

        <S.ButtonsWrapper>
          <Button buttonType="secondary" onClick={handleClose} disabled={isSubmitting}>
            {intl.formatMessage(msgs.cancel)}
          </Button>
          <Button
            buttonType="primary"
            onClick={handleSubmit}
            disabled={!isMessageValid || isSubmitting}
            loading={isSubmitting}
          >
            {intl.formatMessage(msgs.send)}
          </Button>
        </S.ButtonsWrapper>
      </S.Container>
    </Modal>
  );
};
